/**
 * Report Definitions Registry
 *
 * Centralized configuration for all available reports.
 * Add new reports here to make them available in the Reports page.
 *
 * Location restrictions:
 * - availableForLocations: Whitelist - only these locations can run the report
 * - excludedLocations: Blacklist - these locations cannot run the report
 * - If neither specified, report is available at all locations
 */

import { ReportDefinition, ReportDepartmentKey } from '../types/reports';

export const REPORT_DEFINITIONS: ReportDefinition[] = [
  // ===== NEW VEHICLE REPORTS =====
  {
    id: 'new-gross-report',
    title: 'New Selling Gross DOC',
    description: 'New vehicle gross, F&I, and selling gross with variable and semi-fixed expense detail',
    department: 'NEW_VEHICLE',
    excludedLocations: [44], // Body Shop only
  },
  {
    id: 'sales-team-performance',
    title: 'Sales Team Performance',
    description: 'Sales managers and salespeople with gross, holdback, F&I, and unit detail. Data is monthly.',
    department: 'NEW_VEHICLE',
    excludedLocations: [12, 44],
    // DepartmentManager added 2026-07-28: NEW-dept managers at their assigned
    // location(s) only — dept tabs + LocationDropdown scope the frontend, and
    // run-report confirms the assignment server-side (users-table lookup).
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager'],
  },
  {
    id: 'sales-team-funnel-report',
    title: 'Sales Team Report',
    description: 'Sales Funnel (Lead → Visit → Sale) grouped by sales team, for any set of locations — either the last 5 working days ending on a chosen date, or a date range with weekly trend columns. Teams are configured in Configuration → Sales Teams.',
    department: 'NEW_VEHICLE',
    multiLocation: true,
    excludedLocations: [12, 44],
    // Kim approved 2026-08-31. DepartmentManager requires the NEW_VEHICLE
    // department (run-report verifies against the users table) but may run
    // the report for ANY location — funnel counts only, no comp data.
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager'],
  },
  {
    id: 'new-yoy-comparison',
    title: 'Year-over-Year Comparison',
    description: 'Compare current period performance to same period last year',
    department: 'NEW_VEHICLE',
    comingSoon: true,
  },

  // ===== USED VEHICLE REPORTS =====
  {
    id: 'used-gross-report',
    title: 'Used Selling Gross DOC',
    description: 'Used vehicle gross, F&I, and selling gross with variable and semi-fixed expense detail',
    department: 'USED_VEHICLE',
    excludedLocations: [44], // Body Shop only
  },
  {
    id: 'used-yoy-comparison',
    title: 'Year-over-Year Comparison',
    description: 'Compare used vehicle performance to same period last year',
    department: 'USED_VEHICLE',
    comingSoon: true,
  },

  // ===== SERVICE REPORTS =====
  {
    id: 'service-gross-report',
    title: 'Service Selling Gross DOC',
    description: 'Service sales, gross, and selling gross with sales expense detail',
    department: 'SERVICE',
    excludedLocations: [12, 44], // 12=consolidated into Body Shop, 44=Body Shop only
  },
  {
    id: 'service-yoy-comparison',
    title: 'Year-over-Year Comparison',
    description: 'Compare service department performance to prior year',
    department: 'SERVICE',
    excludedLocations: [44],
    comingSoon: true,
  },
  {
    id: 'tsm-ranking-report',
    title: 'TSM Ranking Report',
    description: 'Company-wide Service Advisor leaderboard across 6 performance metrics — printable for posting',
    department: 'SERVICE',
    multiLocation: true,
    excludedLocations: [12, 44],
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager'],
  },
  {
    id: 'tsm-group-ranking-report',
    title: 'TSM Group Ranking Report',
    description: 'Service Advisor leaderboard split into Domestic / Import / Express groups — one page per group for posting. Configure assignments in Configuration → TSM Groups.',
    department: 'SERVICE',
    multiLocation: true,
    excludedLocations: [12, 44],
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager', 'TeamServiceManager'],
  },
  {
    id: 'tsm-items-report',
    title: 'TSM Items Report',
    description: 'Service Advisor leaderboard ranking penetration % of 7 tracked labor-op items: Batteries, Brake Repairs, Brake Services, Cabin Air Filters, Engine Air Filters, Alignments, Wiper Blades.',
    department: 'SERVICE',
    multiLocation: true,
    excludedLocations: [12, 44],
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager', 'TeamServiceManager'],
  },
  {
    id: 'trade-in-report',
    title: 'Trade-In Report',
    description: 'Used vehicles taken in on trade — stock #, vehicle, miles, trade plan, and comments for creating repair orders.',
    department: 'SERVICE',
    excludedLocations: [44], // Body Shop only — no vehicle sales / no Nut Sheet
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager', 'TeamServiceManager'],
  },

  // ===== PARTS REPORTS =====
  {
    id: 'parts-gross-report',
    title: 'Parts Selling Gross DOC',
    description: 'Parts sales, gross, and selling gross with sales expense detail',
    department: 'PARTS',
    excludedLocations: [44], // Undercoating doesn't have Parts
  },
  {
    id: 'parts-yoy-comparison',
    title: 'Year-over-Year Comparison',
    description: 'Compare parts department performance to prior year',
    department: 'PARTS',
    excludedLocations: [44],
    comingSoon: true,
  },
  {
    id: 'parts-counter-performance-report',
    title: 'Parts Counterperson Performance',
    description: 'Per-person gross, transactions, lost sales, and pay-type breakdown across all locations for a selected month range (e.g., YTD).',
    department: 'PARTS',
    consolidatedOnly: true,
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager'],
  },
  {
    id: 'wholesale-comparison',
    title: 'Wholesale Comparison',
    description: 'Compare counter wholesale performance across locations with year-over-year analysis',
    department: 'PARTS',
    excludedLocations: [44],
    multiLocation: true,
  },

  // ===== BODY SHOP REPORTS =====
  {
    id: 'body-gross-report',
    title: 'Body Shop Selling Gross DOC',
    description: 'Body shop gross and selling gross with sales expense detail',
    department: 'BODY_SHOP',
  },
  {
    id: 'body-yoy-comparison',
    title: 'Year-over-Year Comparison',
    description: 'Compare body shop performance to prior year',
    department: 'BODY_SHOP',
    comingSoon: true,
  },

  // ===== RENTAL REPORTS =====
  {
    id: 'rental-revenue-report',
    title: 'Revenue Report',
    description: 'Rental income and utilization metrics',
    department: 'RENTAL',
    availableForLocations: [1, 2, 3, 4, 5], // Only main locations have rental
    comingSoon: true,
  },

  // ===== OTHERS REPORTS =====
  {
    id: 'other-income-report',
    title: 'Other Income Report',
    description: 'Miscellaneous income sources and trends',
    department: 'OTHERS',
    comingSoon: true,
  },

  // ===== MARKETING REPORTS =====
  {
    id: 'ad-expense-comparison',
    title: 'Ad Expense Comparison',
    description: 'Advertising expenses and cost per unit sold across locations with year-over-year analysis',
    department: 'MARKETING',
    excludedLocations: [12, 44],
    multiLocation: true,
  },
  {
    id: 'lead-pacing-report',
    title: 'Lead Pacing Report',
    description: 'Company-wide and per-location MTD leads, units, closing ratio, gross variance, and what each rooftop needs to hit its unit goal.',
    department: 'MARKETING',
    excludedLocations: [12, 44],
    multiLocation: true,
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager', 'MarketingManager'],
  },
  {
    id: 'lead-source-analysis',
    title: 'Lead Source Analysis',
    description: 'Lead sources, conversion rates, and cost per lead',
    department: 'MARKETING',
    comingSoon: true,
  },

  // ===== EXECUTIVE REPORTS =====
  {
    id: 'prediction-scorecard',
    title: 'Prediction Scorecard',
    description: 'GM projections vs two statistical models — monthly gross and unit predictions for every location, scored against actuals as each month closes.',
    department: 'EXECUTIVE',
    multiLocation: true,
    // Not consolidated-only in spirit (it spans all rooftops), but the flag
    // keeps the card clickable when the Consolidated location is selected.
    allowsConsolidated: true,
    // Standings compare GM projection accuracy across rooftops — execs only.
    allowedRoles: ['Admin', 'CEO', 'Executive'],
  },
  {
    id: 'ceo-daily-briefing',
    title: 'Daily Briefing',
    description: 'Daily executive summary: gross, units, MTD, forecast variance, and operational flags',
    department: 'EXECUTIVE',
    allowsConsolidated: true,
    singleDate: true,
    allowedRoles: ['Admin', 'CEO', 'Executive', 'GeneralManager'],
    consolidatedAllowedRoles: ['Admin', 'CEO', 'Executive'],
  },
];

/**
 * Get all reports for a specific department
 */
export function getReportsByDepartment(department: ReportDepartmentKey): ReportDefinition[] {
  return REPORT_DEFINITIONS.filter((r) => r.department === department);
}

/**
 * Get a specific report by ID
 */
export function getReportById(id: string): ReportDefinition | undefined {
  return REPORT_DEFINITIONS.find((r) => r.id === id);
}
